import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Badge, UserBadge } from '../types';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

export const badgeService = {
  // Badge Management (Coach functions)
  
  // Create a new badge template
  async createBadge(
    teamId: string,
    coachId: string,
    badgeData: Omit<Badge, 'id' | 'teamId' | 'coachId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const badgesCollection = collection(db, 'badges');
    
    const newBadge: Omit<Badge, 'id'> = {
      ...badgeData,
      teamId,
      coachId,
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    const docRef = await addDoc(badgesCollection, newBadge);
    return docRef.id;
  },

  // Get all available badge templates for a team
  async getAvailableBadges(teamId: string): Promise<Badge[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'badges',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        const badges = documents.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now()
        })) as Badge[];
        
        return badges.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime; // Descending order
        });
      }
      
      // On web/Android, use SDK
      const q = query(
        collection(db, 'badges'),
        where('teamId', '==', teamId),
        where('isActive', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Sort in JavaScript instead of Firestore
      const badges = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt
      })) as Badge[];
      
      return badges.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
    } catch (error) {
      console.warn('Failed to load available badges:', error);
      return []; // Return empty array if collection doesn't exist or permission denied
    }
  },

  // Update a badge template
  async updateBadge(badgeId: string, updates: Partial<Badge>): Promise<void> {
    const badgeRef = doc(db, 'badges', badgeId);
    await updateDoc(badgeRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  // Delete a badge template (soft delete)
  async deleteBadge(badgeId: string): Promise<void> {
    await this.updateBadge(badgeId, { isActive: false });
  },

  // User Badge Management

  // Get user's earned badges
  async getUserBadges(userId: string, teamId: string): Promise<UserBadge[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'userBadges',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        const badges = documents.map(doc => ({
          id: doc.id,
          ...doc.data(),
          awardedAt: doc.data().awardedAt || Timestamp.now()
        })) as UserBadge[];
        
        return badges.sort((a, b) => {
          const aTime = a.awardedAt?.seconds || 0;
          const bTime = b.awardedAt?.seconds || 0;
          return bTime - aTime; // Descending order
        });
      }
      
      // On web/Android, use SDK
      const q = query(
        collection(db, 'userBadges'),
        where('userId', '==', userId),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Sort in JavaScript instead of Firestore
      const badges = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        awardedAt: doc.data().awardedAt
      })) as UserBadge[];
      
      return badges.sort((a, b) => {
        const aTime = a.awardedAt?.seconds || 0;
        const bTime = b.awardedAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
    } catch (error) {
      console.warn('Failed to load user badges:', error);
      return []; // Return empty array if collection doesn't exist or permission denied
    }
  },

  // Award a badge to a user (Coach function)
  async awardBadgeToUser(
    userId: string,
    teamId: string,
    badgeId: string,
    coachId: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Verify the badge exists and is active
      const badgeRef = doc(db, 'badges', badgeId);
      const badgeDoc = await getDoc(badgeRef);
      
      if (!badgeDoc.exists()) {
        return { success: false, message: 'Badge not found' };
      }
      
      const badge = badgeDoc.data() as Badge;
      if (!badge.isActive || badge.teamId !== teamId) {
        return { success: false, message: 'Badge is not available' };
      }
      
      // Check if user already has this badge
      const existingBadgesQuery = query(
        collection(db, 'userBadges'),
        where('userId', '==', userId),
        where('teamId', '==', teamId),
        where('badgeId', '==', badgeId)
      );
      
      const existingBadges = await getDocs(existingBadgesQuery);
      if (!existingBadges.empty) {
        return { success: false, message: 'User already has this badge' };
      }
      
      // Award the badge
      const userBadgeCollection = collection(db, 'userBadges');
      const newUserBadge: Omit<UserBadge, 'id'> = {
        userId,
        teamId,
        badgeId,
        awardedAt: Timestamp.now(),
        awardedBy: coachId,
        notes
      };
      
      await addDoc(userBadgeCollection, newUserBadge);
      
      return { success: true, message: 'Badge awarded successfully!' };
    } catch (error: any) {
      console.error('Error awarding badge:', error);
      return { success: false, message: 'Failed to award badge. Please try again.' };
    }
  },

  // Remove a badge from a user (Coach function)
  async removeBadgeFromUser(userBadgeId: string): Promise<{ success: boolean; message: string }> {
    try {
      const userBadgeRef = doc(db, 'userBadges', userBadgeId);
      await deleteDoc(userBadgeRef);
      
      return { success: true, message: 'Badge removed successfully!' };
    } catch (error: any) {
      console.error('Error removing badge:', error);
      return { success: false, message: 'Failed to remove badge. Please try again.' };
    }
  },

  // Get all users who have earned a specific badge
  async getBadgeRecipients(teamId: string, badgeId: string): Promise<UserBadge[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'userBadges',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'badgeId', op: '==', value: badgeId }
          ]
        );
        
        // Sort by awardedAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().awardedAt?.seconds || 0;
          const bTime = b.data().awardedAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          awardedAt: doc.data().awardedAt || Timestamp.now()
        })) as UserBadge[];
      } catch (error) {
        console.error('Error fetching badge recipients via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'userBadges'),
      where('teamId', '==', teamId),
      where('badgeId', '==', badgeId),
      orderBy('awardedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      awardedAt: doc.data().awardedAt
    })) as UserBadge[];
  },

  // Get user's badge statistics
  async getUserBadgeStats(userId: string, teamId: string): Promise<{
    totalBadges: number;
    badgesByCategory: Record<string, number>;
    badgesByRarity: Record<string, number>;
  }> {
    const userBadges = await this.getUserBadges(userId, teamId);
    const availableBadges = await this.getAvailableBadges(teamId);
    
    const badgesByCategory: Record<string, number> = {};
    const badgesByRarity: Record<string, number> = {};
    
    userBadges.forEach(userBadge => {
      const badge = availableBadges.find(b => b.id === userBadge.badgeId);
      if (badge) {
        badgesByCategory[badge.category] = (badgesByCategory[badge.category] || 0) + 1;
        badgesByRarity[badge.rarity] = (badgesByRarity[badge.rarity] || 0) + 1;
      }
    });
    
    return {
      totalBadges: userBadges.length,
      badgesByCategory,
      badgesByRarity
    };
  }
};
